import { Button } from "@/components/ui/button";
import { CalendarClock, Loader } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { useMemo, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { addDays, type Locale } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { Field } from "@/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const DATE_FNS_LOCALES: Record<string, Locale> = {
  fr,
  en: enUS,
};

const PRESETS = [
  { labelKey: "keys.preset_today", value: 0 },
  { labelKey: "keys.preset_tomorrow", value: 1 },
  { labelKey: "keys.preset_in_3_days", value: 3 },
  { labelKey: "keys.preset_in_a_week", value: 7 },
  { labelKey: "keys.preset_in_2_weeks", value: 14 },
] as const;

const SchedulerContent = ({
  date,
  setDate,
  currentMonth,
  setCurrentMonth,
  locale,
  isMobile,
  time,
  setTime,
}: {
  date: Date | undefined;
  setDate: (date: Date | undefined) => void;
  currentMonth: Date;
  setCurrentMonth: (date: Date) => void;
  locale: Locale;
  isMobile: boolean;
  time: string;
  setTime: (time: string) => void;
}) => {
  const { t } = useTranslation();
  const today = new Date();

  const presetButtons = (
    <div className="flex flex-wrap gap-2">
      {PRESETS.map((preset) => (
        <Button
          key={preset.value}
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={() => {
            const newDate = addDays(new Date(), preset.value);
            setDate(newDate);
            setCurrentMonth(
              new Date(newDate.getFullYear(), newDate.getMonth(), 1),
            );
          }}
        >
          {t(preset.labelKey)}
        </Button>
      ))}
    </div>
  );

  const timeInput = (
    <Field>
      <InputGroup className="h-12">
        <InputGroupInput
          id="time-from"
          type="time"
          step="1"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          className="appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
        />
        <InputGroupAddon>{t("keys.at")}</InputGroupAddon>
      </InputGroup>
    </Field>
  );

  if (isMobile) {
    return (
      <div className="flex flex-col gap-2 items-center justify-center">
        <Calendar
          mode="single"
          selected={date}
          onSelect={setDate}
          month={currentMonth}
          onMonthChange={setCurrentMonth}
          captionLayout="dropdown"
          locale={locale}
          disabled={{ before: today }}
          formatters={{
            formatMonthDropdown: (date) =>
              date.toLocaleString(locale.code, { month: "short" }),
          }}
        />
        <div className="px-4 py-2 border-y bg-gray-50 w-full">
          {presetButtons}
        </div>
        <div className="px-4 border-b pb-2 w-full">{timeInput}</div>
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      <Calendar
        mode="single"
        selected={date}
        onSelect={setDate}
        month={currentMonth}
        onMonthChange={setCurrentMonth}
        className="rounded-lg bg-gray-50"
        locale={locale}
        disabled={{ before: today }}
      />
      <div className="flex flex-col gap-y-2">
        <div className="p-2 rounded-md bg-gray-50">{presetButtons}</div>
        {timeInput}
      </div>
    </div>
  );
};

function parseInitialDate(initialDate?: string | null) {
  if (!initialDate) return null;
  const parsed = new Date(initialDate);
  if (isNaN(parsed.getTime())) return null;
  return parsed;
}

const DilemmaScheduler = ({
  isLoading,
  onConfirm: onConfirmProp,
  disabled,
  initialDate,
  open: openProp,
  onOpenChange: onOpenChangeProp,
}: {
  isLoading: boolean;
  onConfirm: (scheduledDate: string) => void;
  disabled?: boolean;
  initialDate?: string | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  const setOpen = onOpenChangeProp ?? setInternalOpen;

  const parsedInitial = useMemo(
    () => parseInitialDate(initialDate),
    [initialDate],
  );

  const [date, setDate] = useState<Date | undefined>(
    parsedInitial ?? undefined,
  );
  const [time, setTime] = useState(
    parsedInitial
      ? `${String(parsedInitial.getHours()).padStart(2, "0")}:${String(parsedInitial.getMinutes()).padStart(2, "0")}:${String(parsedInitial.getSeconds()).padStart(2, "0")}`
      : "10:30:00",
  );
  const [currentMonth, setCurrentMonth] = useState<Date>(
    parsedInitial
      ? new Date(parsedInitial.getFullYear(), parsedInitial.getMonth(), 1)
      : new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  );

  const locale = useMemo(
    () => DATE_FNS_LOCALES[i18n.resolvedLanguage ?? "fr"] ?? fr,
    [i18n.resolvedLanguage],
  );

  const handleConfirm = () => {
    if (!date) return;
    const [hours, minutes, seconds] = time.split(":").map(Number);
    const combined = new Date(date);
    combined.setHours(hours, minutes, seconds, 0);
    if (combined <= new Date()) {
      toast.error(t("keys.schedule_past_date_error"));
      return;
    }
    onConfirmProp(combined.toISOString());
    setOpen(false);
  };

  const triggerButton = (
    <Button
      type="button"
      variant="secondary"
      disabled={disabled}
      className="text-gray-600 hover:text-gray-600 justify-start"
    >
      {isLoading ? (
        <Loader className="animate-spin" />
      ) : (
        <CalendarClock className="size-4 text-gray-600" />
      )}
      {t("keys.schedule_publication")}
    </Button>
  );

  const trigger = disabled ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="inline-flex">
          {triggerButton}
        </span>
      </TooltipTrigger>
      <TooltipContent>{t("keys.schedule_disabled_tooltip")}</TooltipContent>
    </Tooltip>
  ) : (
    triggerButton
  );

  const contentProps = {
    date,
    setDate,
    currentMonth,
    setCurrentMonth,
    locale,
    isMobile,
    time,
    setTime,
  };

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        {openProp === undefined && (
          <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        )}
        <DrawerContent>
          <DrawerHeader className="text-left">
            <DrawerTitle>{t("keys.schedule_title")}</DrawerTitle>
            <DrawerDescription className="sr-only">
              {t("keys.schedule_title")}
            </DrawerDescription>
          </DrawerHeader>
          <SchedulerContent {...contentProps} />
          <DrawerFooter className="pt-2 flex-row">
            <DrawerClose asChild>
              <Button className="flex-1" variant="outline">
                {t("keys.cancel")}
              </Button>
            </DrawerClose>
            <Button
              className="flex-1"
              variant="destructive"
              onClick={handleConfirm}
            >
              {t("keys.confirm")}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {openProp === undefined && (
        <DialogTrigger asChild>{trigger}</DialogTrigger>
      )}
      <DialogContent showCloseButton={false} className="sm:max-w-3xlfe">
        <DialogHeader>
          <DialogTitle>{t("keys.schedule_title")}</DialogTitle>
          <DialogDescription className="sr-only">
            {t("keys.schedule_title")}
          </DialogDescription>
        </DialogHeader>
        <SchedulerContent {...contentProps} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("keys.cancel")}</Button>
          </DialogClose>
          <Button variant="destructive" onClick={handleConfirm}>
            {t("keys.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DilemmaScheduler;
